import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  columnOrderingFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createPaginatedRowModel,
  createSortedRowModel,
  type PaginationState,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  type SortingState,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import {
  closestCenter,
  DndContext,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Pagination,
  Paper,
  Popover,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import {
  Calendar,
  SlidersHorizontal,
} from 'lucide-react'
import { FaBan } from 'react-icons/fa'
import {
  getPowerStatus,
  POWER_STATUS_VALUES,
} from '@common/utils/helperFunctions'
import { DebouncedTextFilter } from '@components/general/DataTable/DebouncedTextFilter'
import {
  DragAlongBodyCell,
  DragAlongFilterCell,
  DraggableHeaderCell,
} from '@components/general/DataTable/DraggableCells'
import { useColumnPreferences } from '@components/general/DataTable/useColumnPreferences'
import { useColumnReorder } from '@components/general/DataTable/useColumnReorder'
import {
  formatTableDate,
  includesText,
  matchesDateRange,
  toSortableTime,
} from '@components/main/DeviceManagement/DeviceInformationManager/deviceInformationTable.helpers'
import {
  BATTERY_BAND_VALUES,
  BattIcon,
  getBatteryBands,
} from '@components/ui/BattIcon'
import {
  getSignalBand,
  SIGNAL_BAND_VALUES,
  SignalIcon,
} from '@components/ui/SignalIcon'
import {
  MVNO_VALUES,
  VENDOR_NAME_VALUES,
} from '@data/device/mapDevice'
import { type DeviceBase } from '@data/device/model/device'

const PAGE_SIZE = 30

export type TestBenchVariant = 'staged' | 'test'

const DATA_COLUMN_ORDER = [
  'deviceId',
  'firmware',
  'signalStrength',
  'batteryLevel',
  'lastPing',
  'mvno',
  'networkName',
  'deviceVendorName',
  'batteryStatus',
  'ICCID',
] as const

// Only test devices can be staged, so only the test tab has an actions column. Its id is left
// out of the staged order entirely rather than merely not rendered, so the staged tab's stored
// order can never carry it.
const CANONICAL_COLUMN_ORDER: Record<TestBenchVariant, readonly string[]> = {
  staged: DATA_COLUMN_ORDER,
  test: [...DATA_COLUMN_ORDER, 'actions'],
}

interface TestBenchRow {
  device: DeviceBase
  powerStatus: string
  batteryBands: string[]
  signalBand: string
  lastPing?: number
}

interface TestBenchFilters {
  deviceId: string
  firmware: string
  networkName: string
  ICCID: string
  signalStrength: string | null
  batteryLevel: string | null
  mvno: string | null
  deviceVendorName: string | null
  batteryStatus: string | null
  lastPingFrom: string | null
  lastPingTo: string | null
}

const EMPTY_FILTERS: TestBenchFilters = {
  deviceId: '',
  firmware: '',
  networkName: '',
  ICCID: '',
  signalStrength: null,
  batteryLevel: null,
  mvno: null,
  deviceVendorName: null,
  batteryStatus: null,
  lastPingFrom: null,
  lastPingTo: null,
}

type TextFilterKey = 'deviceId' | 'firmware' | 'networkName' | 'ICCID'

type SelectFilterKey = 'signalStrength' | 'batteryLevel' | 'mvno' | 'deviceVendorName' | 'batteryStatus'

const TEXT_FILTER_KEYS: readonly string[] = [
  'deviceId',
  'firmware',
  'networkName',
  'ICCID',
]

// Each select offers exactly the values its cell can display, so no option is unselectable
// and no displayed value is unreachable.
const SELECT_FILTER_OPTIONS: Record<SelectFilterKey, string[]> = {
  signalStrength: [...SIGNAL_BAND_VALUES],
  batteryLevel: [...BATTERY_BAND_VALUES],
  mvno: MVNO_VALUES,
  deviceVendorName: VENDOR_NAME_VALUES,
  batteryStatus: [...POWER_STATUS_VALUES],
}

const isTextFilterKey = (columnId: string): columnId is TextFilterKey =>
  TEXT_FILTER_KEYS.includes(columnId)

const isSelectFilterKey = (columnId: string): columnId is SelectFilterKey =>
  columnId in SELECT_FILTER_OPTIONS

// Text and date matching reuse Device Management's helpers rather than re-deriving them: the
// date range compares 'YYYY-MM-DD' strings through startOfDay/endOfDay, which is what fixed a
// day shift for users west of UTC.
const matchesFilters = (row: TestBenchRow, filters: TestBenchFilters) => {
  const { device } = row

  if (!includesText(device.deviceId, filters.deviceId)) return false
  if (!includesText(device.firmware, filters.firmware)) return false
  if (!includesText(device.networkName, filters.networkName)) return false
  if (!includesText(device.ICCID, filters.ICCID)) return false
  if (filters.signalStrength && row.signalBand !== filters.signalStrength) return false
  // A device can show an internal and an external battery reading, so it matches a band on
  // either — the same rule Device Management applies.
  if (filters.batteryLevel && !row.batteryBands.includes(filters.batteryLevel)) return false
  if (filters.mvno && device.mvno !== filters.mvno) return false
  if (filters.deviceVendorName && device.deviceVendorName !== filters.deviceVendorName) return false
  if (filters.batteryStatus && row.powerStatus !== filters.batteryStatus) return false

  return matchesDateRange(device.lastOnline, filters.lastPingFrom, filters.lastPingTo)
}

const features = tableFeatures({
  columnOrderingFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  rowPaginationFeature,
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  // Columns without an explicit sortFn resolve via 'auto', which only finds
  // functions registered here. An unregistered name silently degrades.
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
})

const columnHelper = createColumnHelper<typeof features, TestBenchRow>()

type TestBenchTableProps =
  | {
    variant: 'staged'
    devices: DeviceBase[]
  }
  | {
    variant: 'test'
    devices: DeviceBase[]
    onStageDevice: (deviceId: string) => void
    onDeviceClick: (device: DeviceBase) => void
  }

export const TestBenchTable = (props: TestBenchTableProps) => {
  const {
    variant,
    devices,
  } = props
  const onStageDevice = props.variant === 'test' ? props.onStageDevice : undefined
  const onDeviceClick = props.variant === 'test' ? props.onDeviceClick : undefined

  const [sorting, setSorting] = useState<SortingState>([])
  // Local state, deliberately not URL parameters: both tabs live on /test-bench, so one URL
  // namespace would make the two filter rows the same control. Not persisted either — a stale
  // filter restored on load reads as missing data.
  const [filters, setFilters] = useState<TestBenchFilters>(EMPTY_FILTERS)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  })

  const {
    columnOrder,
    columnSizing,
    columnVisibility,
    handleColumnOrderChange,
    handleColumnSizingChange,
    handleColumnVisibilityChange,
    storageAvailable,
  } = useColumnPreferences({
    storageKeys: {
      visibility: `test-bench-${variant}-column-visibility-v1`,
      sizing: `test-bench-${variant}-column-sizing-v1`,
      order: `test-bench-${variant}-column-order-v1`,
    },
    canonicalColumnOrder: CANONICAL_COLUMN_ORDER[variant],
    storageProbeKey: `test-bench-${variant}-storage-probe`,
    storageFailureMessage: 'Table preferences can\'t be saved in this browser — your column selection, widths, and order will reset when you reload.',
  })

  const rows = useMemo<TestBenchRow[]>(
    () => devices.map((device) => ({
      device,
      // Gated here rather than in the cell, as in Device Management, so the Power filter and
      // the rendered value are the same string by construction.
      powerStatus: device.batteryStatus ? getPowerStatus(device) : '',
      batteryBands: getBatteryBands(device),
      signalBand: getSignalBand(device.signal),
      lastPing: toSortableTime(device.lastOnline),
    })),
    [devices],
  )

  const matchingRows = useMemo(
    () => rows.filter((row) => matchesFilters(row, filters)),
    [filters, rows],
  )

  // Any filter change returns to the first page, as Device Management does.
  const updateFilters = (updates: Partial<TestBenchFilters>) => {
    setFilters((previous) => ({
      ...previous,
      ...updates,
    }))
    setPagination((previous) => ({
      ...previous,
      pageIndex: 0,
    }))
  }

  const columns = useMemo(() => columnHelper.columns([
    columnHelper.accessor((row) => row.device.deviceId, {
      id: 'deviceId',
      header: 'ID',
      enableHiding: false,
      size: 200,
    }),
    columnHelper.accessor((row) => row.device.firmware, {
      id: 'firmware',
      header: 'Firmware',
      size: 180,
    }),
    columnHelper.accessor((row) => row.device.signal, {
      id: 'signalStrength',
      header: 'Signal',
      size: 150,
      sortUndefined: 'last',
      cell: ({ row }) => <SignalIcon signalLevel={row.original.device.signal} />,
    }),
    columnHelper.accessor((row) => row.device.batteryLevel, {
      id: 'batteryLevel',
      header: 'Battery',
      size: 180,
      sortUndefined: 'last',
      cell: ({ row }) => <BattIcon device={row.original.device} />,
    }),
    columnHelper.accessor('lastPing', {
      header: 'Last Check In',
      size: 190,
      sortUndefined: 'last',
      cell: ({ getValue }) => formatTableDate(getValue(), true),
    }),
    columnHelper.accessor((row) => row.device.mvno || '-', {
      id: 'mvno',
      header: 'MVNO',
      size: 150,
    }),
    columnHelper.accessor((row) => row.device.networkName, {
      id: 'networkName',
      header: 'Network',
      size: 160,
      sortUndefined: 'last',
    }),
    columnHelper.accessor((row) => row.device.deviceVendorName, {
      id: 'deviceVendorName',
      header: 'Vendor',
      size: 150,
      sortUndefined: 'last',
    }),
    columnHelper.accessor('powerStatus', {
      id: 'batteryStatus',
      header: 'Power',
      size: 160,
      cell: ({ row }) => row.original.powerStatus || <FaBan />,
    }),
    columnHelper.accessor((row) => row.device.ICCID, {
      id: 'ICCID',
      header: 'ICCID',
      size: 220,
      sortUndefined: 'last',
    }),
    ...(onStageDevice
      ? [
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          size: 140,
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }) => (
            <Button
              size='xs'
              onClick={(event) => {
                // The row opens the device modal on click; staging must not open it too.
                event.stopPropagation()
                onStageDevice(row.original.device.deviceId)
              }}
            >
              Stage Device
            </Button>
          ),
        }),
      ]
      : []),
  ]), [onStageDevice])

  const table = useTable({
    features,
    data: matchingRows,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnSizing,
      columnOrder,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnOrderChange: handleColumnOrderChange,
    onPaginationChange: setPagination,
    getRowId: (row) => row.device.deviceId,
    // The page is reset explicitly on a filter change and clamped below when the row count
    // shrinks, so TanStack's own reset stays off, as in Device Management.
    autoResetPageIndex: false,
    columnResizeMode: 'onChange',
  })

  const pageCount = table.getPageCount()

  // Filtering or a refetch can shrink the row count while a later page is showing, which would
  // otherwise leave an empty table.
  useEffect(() => {
    const lastPageIndex = Math.max(pageCount, 1) - 1
    if (pagination.pageIndex <= lastPageIndex) return

    setPagination((previous) => ({
      ...previous,
      pageIndex: lastPageIndex,
    }))
  }, [pageCount, pagination.pageIndex])

  const visibleColumns = table.getVisibleLeafColumns()
  const visibleColumnIds = visibleColumns.map((column) => column.id)
  const {
    handleColumnDragEnd,
    handleKeyboardColumnMove,
    sensors,
  } = useColumnReorder(handleColumnOrderChange, visibleColumnIds)

  const orderedHideableColumns = columnOrder.flatMap((id) => {
    const column = table.getColumn(id)
    return column?.getCanHide() ? [column] : []
  })

  const renderFilter = (columnId: string) => {
    if (isTextFilterKey(columnId)) {
      return (
        <DebouncedTextFilter
          ariaLabel={`Filter ${columnId}`}
          value={filters[columnId]}
          onChange={(value) => updateFilters({ [columnId]: value })}
        />
      )
    }

    if (isSelectFilterKey(columnId)) {
      return (
        <Select
          aria-label={`Filter ${columnId}`}
          placeholder='All'
          size='xs'
          clearable
          data={SELECT_FILTER_OPTIONS[columnId]}
          value={filters[columnId]}
          onChange={(value) => updateFilters({ [columnId]: value })}
        />
      )
    }

    if (columnId === 'lastPing') {
      return (
        <DatePickerInput
          aria-label='Filter lastPing by date range'
          type='range'
          placeholder='Select range...'
          size='xs'
          value={[filters.lastPingFrom, filters.lastPingTo]}
          onChange={(value) => updateFilters({
            lastPingFrom: value[0],
            lastPingTo: value[1],
          })}
          leftSection={<Calendar size={12} />}
          clearable
        />
      )
    }

    return null
  }

  const paginatedRows = table.getRowModel().rows
  const matchingCount = matchingRows.length
  const firstVisible = matchingCount === 0 ? 0 : pagination.pageIndex * PAGE_SIZE + 1
  const lastVisible = Math.min((pagination.pageIndex + 1) * PAGE_SIZE, matchingCount)

  return (
    <Box
      data-testid={`test-bench-${variant}-table`}
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <Group
        align='center'
        gap='sm'
        my='md'
      >
        <Popover
          position='bottom-start'
          withArrow
          shadow='md'
        >
          <Popover.Target>
            <Box
              component='span'
              ml='auto'
            >
              <Tooltip label='Table settings' withArrow>
                <Button
                  aria-label='Table settings'
                  size='sm'
                  variant='light'
                  px={{ base: 'xs', sm: 'md' }}
                >
                  <Group gap='xs' wrap='nowrap'>
                    <SlidersHorizontal
                      aria-hidden
                      size={14}
                    />
                    <Box
                      component='span'
                      visibleFrom='sm'
                    >
                      Settings
                    </Box>
                  </Group>
                </Button>
              </Tooltip>
            </Box>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap='xs' miw={220}>
              <Text
                size='xs'
                fw={600}
                c='dimmed'
                tt='uppercase'
              >
                Toggle columns
              </Text>
              <ScrollArea.Autosize
                data-testid={`test-bench-${variant}-column-checkbox-scroll`}
                mah='50vh'
                type='auto'
                scrollbarSize={8}
                viewportProps={{ id: `test-bench-${variant}-column-checkbox-scroll-viewport` }}
              >
                <Stack gap='xs' pr='xs'>
                  {orderedHideableColumns.map((column) => (
                    <Checkbox
                      key={column.id}
                      label={typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id}
                      checked={column.getIsVisible()}
                      onChange={column.getToggleVisibilityHandler()}
                      size='sm'
                    />
                  ))}
                </Stack>
              </ScrollArea.Autosize>
              {!storageAvailable ? (
                <Text size='xs' c='yellow.8'>
                  Column selection, widths, and order will reset when this page reloads.
                </Text>
              ) : null}
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Group>

      <Paper
        shadow='sm'
        radius='md'
        withBorder
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <Table.ScrollContainer
          minWidth={1200}
          type='native'
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            sensors={sensors}
            onDragEnd={handleColumnDragEnd}
          >
            <Table
              striped
              highlightOnHover
              stickyHeader
              layout='fixed'
              horizontalSpacing={10}
              verticalSpacing={8}
              styles={{
                thead: {
                  boxShadow: '0 2px 4px rgb(0 0 0 / 10%)',
                },
                th: {
                  fontSize: 12,
                },
              }}
              style={{
                width: table.getTotalSize(),
              }}
            >
              <Table.Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Fragment key={headerGroup.id}>
                    <Table.Tr>
                      <SortableContext
                        items={visibleColumnIds}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DraggableHeaderCell
                            key={header.id}
                            header={header}
                            onKeyboardMove={handleKeyboardColumnMove}
                          />
                        ))}
                      </SortableContext>
                    </Table.Tr>

                    <Table.Tr>
                      <SortableContext
                        items={visibleColumnIds}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DragAlongFilterCell
                            key={`${header.id}-filter`}
                            header={header}
                          >
                            {renderFilter(header.column.id)}
                          </DragAlongFilterCell>
                        ))}
                      </SortableContext>
                    </Table.Tr>
                  </Fragment>
                ))}
              </Table.Thead>

              <Table.Tbody>
                {paginatedRows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={visibleColumns.length}>
                      <Text
                        ta='center'
                        c='dimmed'
                        py='xl'
                      >
                        No devices found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : paginatedRows.map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={onDeviceClick ? { cursor: 'pointer' } : undefined}
                    onClick={onDeviceClick ? () => onDeviceClick(row.original.device) : undefined}
                  >
                    <SortableContext
                      items={visibleColumnIds}
                      strategy={horizontalListSortingStrategy}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <DragAlongBodyCell
                          key={cell.id}
                          cell={cell}
                        />
                      ))}
                    </SortableContext>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </DndContext>
        </Table.ScrollContainer>

        {matchingCount > 0 ? (
          <>
            <Divider />
            <Group
              justify='space-between'
              p='md'
            >
              <Text
                size='md'
                fw={600}
                c='var(--mantine-color-text)'
              >
                {`Showing ${firstVisible}–${lastVisible} of ${matchingCount} devices`}
              </Text>

              {pageCount > 1 ? (
                <Pagination
                  size='sm'
                  total={pageCount}
                  value={pagination.pageIndex + 1}
                  onChange={(page) => table.setPageIndex(page - 1)}
                />
              ) : null}
            </Group>
          </>
        ) : null}
      </Paper>
    </Box>
  )
}
